"use client";

// Botón "pill" (`body.pm-pills .btn { border-radius: 999px }`) + flecha
// deslizante (`.pm-arr`, `transform: translateX(4px)` on hover) — el
// tweak `pillBtns` está ON por default y agrega la flecha a
// `.hero-ctas .btn, .cta-ctas .btn, .price .btn, .nav-cta .btn-primary`.

import Link from "next/link";
import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { L, SHADOWS } from "./tokens";

type Variant = "violet" | "outline" | "ghost-on-dark" | "primary-compact" | "ghost-compact";

const BASE = "inline-flex items-center justify-center gap-2 rounded-full font-medium transition-transform whitespace-nowrap";

export function PillButton({
  href, variant, size = "lg", arrow = true, children, external, fullWidth,
}: {
  href: string;
  variant: Variant;
  size?: "lg" | "compact";
  arrow?: boolean;
  children: React.ReactNode;
  external?: boolean;
  fullWidth?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const sizeClass = size === "lg" ? "text-[15.5px] py-3.5 pl-6 pr-[22px]" : "text-[14.5px] py-2 px-[18px]";

  let style: React.CSSProperties = {};
  if (variant === "violet") {
    style = { background: L.violet, color: "#fff", boxShadow: SHADOWS.violetBtn };
  } else if (variant === "outline") {
    style = hover
      ? { background: L.ink, color: "#fff", border: `1px solid ${L.ink}` }
      : { background: "#fff", color: L.ink, border: `1px solid ${L.lineStrong}` };
  } else if (variant === "ghost-on-dark") {
    style = { color: "#fff", background: hover ? "rgba(255,255,255,.06)" : "transparent" };
  } else if (variant === "primary-compact") {
    style = { background: L.ink, color: "#fff", boxShadow: "0 1px 0 rgba(255,255,255,0.15) inset, 0 6px 18px -8px rgba(21,16,61,0.45)" };
  } else if (variant === "ghost-compact") {
    style = { color: L.ink, background: hover ? "rgba(21,16,61,0.04)" : "transparent" };
  }

  const Comp = external ? "a" : Link;
  return (
    <Comp
      href={href}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`${BASE} ${sizeClass} hover:-translate-y-px${fullWidth ? " w-full" : ""}`}
      style={style}
    >
      {children}
      {arrow && (
        <ArrowRight
          size={15}
          strokeWidth={2.4}
          style={{ transition: "transform .22s cubic-bezier(.34,1.56,.64,1)", transform: hover ? "translateX(4px)" : "none", flexShrink: 0 }}
        />
      )}
    </Comp>
  );
}
